import { useEffect, useMemo, useRef, useState } from "react";
import { BoardCanvas } from "./BoardCanvas";
import { TopHud, BottomHud } from "./Hud";
import { Instructions } from "./Instructions";
import { TutorialReferenceRail } from "./TutorialReferenceRail";
import { theme, FONT_RUNE } from "./theme";
import { useGame } from "./store";
import { useIsWide } from "./useIsWide";
import { generateTutorialScript } from "../tutorial/generateScript";
import type { GamePhase, TutorialStep, TutorialReview } from "../tutorial/script";

/**
 * Below this width (px), the rail starts collapsed by default so the board doesn't get
 * squeezed between two fixed-width sidebars on the small end of the "wide" range.
 */
const TUTORIAL_RAIL_AUTO_OPEN_PX = 1100;

export function TutorialMode({ onExit }: { onExit: () => void }) {
  const newGame = useGame((s) => s.newGame);
  const applyExternalTurn = useGame((s) => s.applyExternalTurn);
  const undoLastTurn = useGame((s) => s.undoLastTurn);
  const game = useGame((s) => s.game);

  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState<"intro" | "applied">("intro");
  const startedRef = useRef(false);
  const isWide = useIsWide();
  // Rail starts collapsed on the small end of wide so the board doesn't get squeezed between
  // a 260px rail and a 360px panel. Default chosen once at mount; user can toggle freely.
  const [railCollapsed, setRailCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < TUTORIAL_RAIL_AUTO_OPEN_PX,
  );
  // Narrow layout uses the existing Instructions modal as the reference surface.
  const [referenceOpen, setReferenceOpen] = useState(false);

  // Generate the scripted game once. Memoized so it survives strict-mode double-render.
  // 40 turns is enough for the greedy bot to demonstrate every piece type. With the
  // historical (orthogonal) Triangle move, Squares dominate the early game via their
  // 3-rank knight-fly; triangles don't see action until the Square rows have advanced,
  // which happens around turn 30. Less than 40 turns and the tutorial silently omits the
  // Triangle move rule explanation entirely.
  const { initialState, steps } = useMemo(() => generateTutorialScript(40), []);

  // Initialize the engine state once we have the script
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    newGame({ mode: "tutorial", initialState });
  }, [newGame, initialState]);

  const step: TutorialStep | undefined = steps[stepIndex];
  const isLast = stepIndex >= steps.length - 1;

  const handleAdvance = () => {
    if (!step) return;

    if (phase === "intro") {
      // Apply the move + optional capture
      applyExternalTurn(step.move, step.declared, step.advance ?? false);
      setPhase("applied");
      return;
    }

    // Move to the next step
    if (isLast) {
      onExit();
      return;
    }
    setStepIndex(stepIndex + 1);
    setPhase("intro");
  };

  // Step backward through the tutorial. Mirrors handleAdvance:
  //   applied → intro of same step  (undo the move; show its intro narration again)
  //   intro   → applied of previous step  (no state change; we're already past that move)
  const canGoBack = phase === "applied" || stepIndex > 0;
  const handleBack = () => {
    if (phase === "applied") {
      undoLastTurn();
      setPhase("intro");
      return;
    }
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
      setPhase("applied");
    }
  };

  // The narration paragraphs split: first paragraph shown pre-move, the rest post-move.
  const showPre = phase === "intro";
  const paragraphs = step
    ? showPre
      ? step.narration.slice(0, 1)
      : step.narration.slice(1)
    : [];

  const buttonLabel = step
    ? showPre
      ? step.declared
        ? "Play move + capture"
        : "Play move"
      : isLast
        ? "Finish"
        : `Next · step ${stepIndex + 2} of ${steps.length}`
    : "";

  const panelProps = step
    ? {
        step,
        paragraphs,
        showPre,
        buttonLabel,
        onAdvance: handleAdvance,
        onBack: handleBack,
        canGoBack,
        stepIndex,
        total: steps.length,
        gameVictory: !!game.victory,
      }
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", position: "relative" }}>
      <TopHud onExit={onExit} />
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: isWide ? "row" : "column",
          minHeight: 0,
          minWidth: 0,
          // BoardCanvas caps itself at calc(50cqh + 16px), which only resolves when an ancestor
          // declares a size container. Without this, the board widens unbounded and floats to
          // the left of any sibling. (GameScreen sets the same on its board row.)
          containerType: "size",
        }}
      >
        {/* Wide-only: persistent reference rail docked on the left. */}
        {isWide && (
          <TutorialReferenceRail
            collapsed={railCollapsed}
            onToggleCollapse={() => setRailCollapsed((c) => !c)}
          />
        )}
        {/* Center the board in the leftover space — sheet layout (narrow) gets a full row
            with the panel overlayed via position:absolute; sidebar layout (wide) gets the
            board between the rail and the tutorial panel, centered within its flex track. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <BoardCanvas />
        </div>
        {isWide && panelProps && (
          <TutorialPanel layout="sidebar" {...panelProps} onOpenReference={() => setReferenceOpen(true)} />
        )}
        {!isWide && panelProps && (
          <TutorialPanel layout="sheet" {...panelProps} onOpenReference={() => setReferenceOpen(true)} />
        )}
      </div>
      <BottomHud />
      {/* Narrow-layout reference: re-uses the existing Instructions modal. Passing no CTAs
          hides the "Jump to game" / "Watch tutorial" buttons (Instructions already gates that
          block on at least one CTA being passed). */}
      <Instructions open={referenceOpen} onClose={() => setReferenceOpen(false)} />
    </div>
  );
}

interface TutorialPanelProps {
  step: TutorialStep;
  paragraphs: string[];
  showPre: boolean;
  buttonLabel: string;
  onAdvance: () => void;
  onBack: () => void;
  canGoBack: boolean;
  stepIndex: number;
  total: number;
  gameVictory: boolean;
  layout: "sheet" | "sidebar";
  /** Opens the Instructions modal — used on narrow layouts where the rail isn't shown. */
  onOpenReference: () => void;
}

function TutorialPanel({
  step,
  paragraphs,
  showPre,
  buttonLabel,
  onAdvance,
  onBack,
  canGoBack,
  stepIndex,
  total,
  gameVictory,
  layout,
  onOpenReference,
}: TutorialPanelProps) {
  const sidebar = layout === "sidebar";

  const containerStyle: React.CSSProperties = sidebar
    ? {
        width: 360,
        flexShrink: 0,
        background: theme.bgElev,
        borderLeft: `1px solid ${theme.accent}`,
        padding: 20,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }
    : {
        position: "absolute",
        left: 8,
        right: 8,
        bottom: 12,
        background: theme.bgElev,
        border: `1px solid ${theme.accent}`,
        borderRadius: 10,
        padding: 14,
        boxShadow: "0 6px 22px rgba(0,0,0,0.6)",
        maxHeight: "55%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      };

  return (
    <div style={containerStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="rune" style={{ fontSize: 12, color: theme.accentSoft, letterSpacing: 2 }}>
            TUTORIAL · {stepIndex + 1}/{total}
          </div>
          {/* "?" reference toggle — only meaningful on narrow (sheet) layout where the rail
              isn't rendered. On wide we hide it; the rail is already visible. */}
          {!sidebar && (
            <button
              onClick={onOpenReference}
              aria-label="Open reference"
              title="Open reference"
              style={referenceToggleButton}
            >
              ?
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Phase badge sits before the rule-of-thumb highlight pills to keep "we're in the
              opening" framing visually distinct from "this move triggered first Assault" etc. */}
          <PhaseBadge phase={step.phase} />
          {step.highlights.map((h, i) => (
            <span
              key={i}
              style={{
                background: theme.bg,
                color: theme.accentSoft,
                fontSize: 10,
                letterSpacing: 1,
                padding: "2px 8px",
                borderRadius: 999,
                border: `1px solid ${theme.boardEdge}`,
                fontFamily: FONT_RUNE,
              }}
            >
              {h.toUpperCase()}
            </span>
          ))}
        </div>
      </div>

      <ProgressDots index={stepIndex} total={total} gameVictory={gameVictory} />

      <div className="rune" style={{ color: theme.parchment, fontSize: sidebar ? 20 : 16, margin: "12px 0 10px" }}>
        {step.title}
      </div>
      <div style={{ flex: sidebar ? 1 : "0 0 auto", minHeight: 0 }}>
        {paragraphs.map((p, i) => (
          <p key={i} style={{ margin: "0 0 10px", color: theme.parchment, fontSize: sidebar ? 15 : 14, lineHeight: 1.6, opacity: 0.92 }}>
            {p}
          </p>
        ))}
        {/* "Why" block — only meaningful pre-move (we're about to do this, and here's the
            plan). Skips silently when computeWhy returned undefined. */}
        {showPre && step.why && (
          <LabeledNote label="Why" tone="accent" text={step.why} />
        )}
        {/* Engine review — shown after the move applies (so the player has seen what happened
            first). Color-coded by classification so a quick scan tells you whether the move
            was solid or a mistake. */}
        {step.review && !showPre && <EngineReviewCard review={step.review} />}
        {/* "Watch for" block — post-move forward look. Skips silently when no meaningful new
            threat appeared on the opponent's side. */}
        {!showPre && step.watchFor && (
          <LabeledNote label="Watch for" tone="warn" text={step.watchFor} />
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          onClick={onBack}
          disabled={!canGoBack}
          style={{
            ...backButton,
            cursor: canGoBack ? "pointer" : "not-allowed",
            opacity: canGoBack ? 1 : 0.35,
          }}
          title={canGoBack ? "Step backward" : "At the start of the tutorial"}
          aria-label="Step backward"
        >
          ← Back
        </button>
        <button onClick={onAdvance} style={advanceButton}>
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

function ProgressDots({ index, total, gameVictory }: { index: number; total: number; gameVictory: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        justifyContent: "flex-start",
        pointerEvents: "none",
        marginTop: 4,
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: i < index ? theme.legalMove : i === index ? theme.accent : theme.boardEdge,
            opacity: gameVictory && i >= index ? 0.4 : 1,
            transition: "background-color 200ms ease",
          }}
        />
      ))}
    </div>
  );
}

const advanceButton: React.CSSProperties = {
  flex: 1,
  minHeight: 44,
  padding: "12px 14px",
  background: theme.accent,
  color: theme.bg,
  border: `1px solid ${theme.accent}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 14,
  letterSpacing: 1,
  cursor: "pointer",
};

const backButton: React.CSSProperties = {
  flexShrink: 0,
  minHeight: 44,
  padding: "12px 16px",
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 14,
  letterSpacing: 1,
};

/**
 * Engine-review pill that follows the post-move narration. Color-coded by classification so a
 * quick scan tells the player how the move stacks up against the engine's preferred line.
 */
function EngineReviewCard({ review }: { review: TutorialReview }) {
  const palette = colorForClass(review.classification);
  return (
    <div
      style={{
        marginTop: 8,
        padding: "10px 12px",
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderLeft: `3px solid ${palette.border}`,
        borderRadius: 6,
        color: theme.parchment,
        fontSize: 13,
        lineHeight: 1.5,
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <span
        className="rune"
        style={{
          fontSize: 10,
          letterSpacing: 1.2,
          color: palette.border,
          textTransform: "uppercase",
          padding: "2px 8px",
          background: theme.bg,
          borderRadius: 999,
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {review.classification}
      </span>
      <span>{review.engineNote}</span>
    </div>
  );
}

function colorForClass(c: TutorialReview["classification"]): { bg: string; border: string } {
  // Match theme tones: positive moves get the legal-move green; negative moves get the
  // capture-flash red; in-between moves use accent gold. All backgrounds at low opacity so
  // they sit on the bgElev card without shouting.
  switch (c) {
    case "best":
    case "great":
      return { bg: "rgba(108, 138, 74, 0.18)", border: theme.legalMove };
    case "good":
      return { bg: "rgba(216, 167, 99, 0.14)", border: theme.accentSoft };
    case "inaccuracy":
      return { bg: "rgba(184, 132, 58, 0.16)", border: theme.accent };
    case "mistake":
    case "blunder":
      return { bg: "rgba(230, 57, 70, 0.16)", border: theme.captureFlash };
  }
}

/**
 * Generic labelled-note block — used for the "Why" pre-move framing and the "Watch for"
 * post-move forward look. Tone controls the accent color (accent = teacher voice / strategic;
 * warn = forward-look pressure).
 */
function LabeledNote({ label, text, tone }: { label: string; text: string; tone: "accent" | "warn" }) {
  const color = tone === "accent" ? theme.accentSoft : theme.captureFlash;
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 12px",
        background: theme.bg,
        border: `1px solid ${theme.boardEdge}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 6,
        color: theme.parchment,
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <span
        className="rune"
        style={{
          fontSize: 10,
          letterSpacing: 1.2,
          color,
          textTransform: "uppercase",
          marginRight: 8,
        }}
      >
        {label}
      </span>
      <span style={{ fontStyle: "italic", opacity: 0.95 }}>{text}</span>
    </div>
  );
}

/** Phase pill — sits ahead of the rule-of-thumb highlights so the player can orient at a glance. */
function PhaseBadge({ phase }: { phase: GamePhase }) {
  const label =
    phase === "opening"
      ? "OPENING"
      : phase === "early-midgame"
        ? "EARLY MIDGAME"
        : phase === "late-midgame"
          ? "LATE MIDGAME"
          : "ENDGAME";
  return (
    <span
      style={{
        background: theme.boardLabel,
        color: theme.bg,
        fontSize: 10,
        letterSpacing: 1,
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${theme.boardLabel}`,
        fontFamily: FONT_RUNE,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

const referenceToggleButton: React.CSSProperties = {
  background: theme.boardEdge,
  color: theme.accentSoft,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 999,
  width: 24,
  height: 24,
  fontFamily: FONT_RUNE,
  fontSize: 14,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  lineHeight: 1,
};
